import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'wouter';
import { useGetDailyPuzzle, useGetUnlimitedPuzzle, useSubmitGuess, useCompletePuzzle, useGetMyStanding, getGetDailyPuzzleQueryKey, getGetUnlimitedPuzzleQueryKey, getGetMyStandingQueryKey, getGetMeQueryKey } from '@workspace/api-client-react';
import { useQueryClient } from '@tanstack/react-query';
import type { Puzzle, PuzzleRound, GuessResult } from '@workspace/api-client-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Show, useUser } from '@clerk/react';
import { Navbar } from '@/components/Navbar';
import { useToast } from '@/hooks/use-toast';
import { Timer, Trophy, Flame, Share2, ArrowRight, RotateCcw, Home, CheckCircle2, XCircle, Zap, Crosshair, ChevronDown, ChevronUp } from 'lucide-react';
import { StreakBadges } from '@/components/StreakBadges';
import { BadgeUnlockCelebration } from '@/components/BadgeUnlockCelebration';
import { Link } from 'wouter';

export default function Play() {
  const params = useParams();
  const slug = params.slug!;
  const mode = params.mode as 'daily' | 'unlimited';
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const isDailyMode = mode === 'daily';
  const { isLoaded: authLoaded, isSignedIn } = useUser();

  const { data: dailyPuzzle, isLoading: dailyLoading } = useGetDailyPuzzle(slug, {
    query: { enabled: isDailyMode && !!slug && authLoaded && !!isSignedIn, queryKey: getGetDailyPuzzleQueryKey(slug) },
  });

  const { data: unlimitedPuzzle, isLoading: unlimitedLoading, refetch: refetchUnlimited } = useGetUnlimitedPuzzle(slug, {
    query: { enabled: !isDailyMode && !!slug, queryKey: getGetUnlimitedPuzzleQueryKey(slug), staleTime: 0, gcTime: 0, refetchOnMount: 'always' },
  });

  const puzzle = isDailyMode ? dailyPuzzle : unlimitedPuzzle;
  const isLoading = isDailyMode ? dailyLoading : unlimitedLoading;

  const [currentRoundIndex, setCurrentRoundIndex] = useState(0);
  const [timeMs, setTimeMs] = useState(0);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [roundStartTime, setRoundStartTime] = useState(Date.now());
  const [roundResults, setRoundResults] = useState<(GuessResult & { choice: string; prompt: string })[]>([]);
  const [showRecap, setShowRecap] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [playResult, setPlayResult] = useState<any>(null);
  // Milestone badges unlocked by THIS play — drives the celebration overlay.
  const [unlockedBadges, setUnlockedBadges] = useState<number[]>([]);
  const queryClient = useQueryClient();
  // Fetched only once the run is complete, so the ranks include this play.
  const { data: standing } = useGetMyStanding(
    { gameSlug: slug },
    { query: { enabled: !!slug && authLoaded && !!isSignedIn && !!playResult, queryKey: getGetMyStandingQueryKey({ gameSlug: slug }) } }
  );
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<GuessResult | null>(null);
  const [revealProgress, setRevealProgress] = useState(0);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Exact accumulated answer time across rounds (ms), independent of the
  // coarse 1s display tick.
  const elapsedRef = useRef(0);

  const submitGuess = useSubmitGuess();
  const completePuzzle = useCompletePuzzle();

  useEffect(() => {
    if (!puzzle || completed) return;
    // 1s resolution is plenty here — this drives the HUD seconds counter and
    // the total-time tally. Ticking at 100ms re-rendered the whole page 10x
    // per second, which made gameplay feel laggy on phones.
    timerRef.current = setInterval(() => {
      setTimeMs((prev) => prev + 1000);
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [puzzle, completed]);

  useEffect(() => {
    if ((puzzle?.gameType === 'logo-reveal' || puzzle?.gameType === 'face-zoom') && !completed && !feedback) {
      // 2 points per tick at 200ms = same 10s reveal, half the re-renders.
      const interval = setInterval(() => {
        setRevealProgress((prev) => Math.min(prev + 2, 100));
      }, 200);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [puzzle?.gameType, completed, feedback]);

  useEffect(() => {
    setRoundStartTime(Date.now());
    setRevealProgress(0);
  }, [currentRoundIndex]);

  if (isDailyMode && authLoaded && !isSignedIn) {
    return (
      <div className="flex min-h-[100dvh] flex-col bg-background">
        <Navbar />
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="w-full max-w-md text-center border border-primary/30 p-8 relative overflow-hidden bg-background">
            <div className="absolute inset-0 bg-[linear-gradient(hsl(189_100%_50%_/_0.1)_1px,_transparent_1px),_linear-gradient(90deg,_hsl(189_100%_50%_/_0.1)_1px,_transparent_1px)] bg-[size:20px_20px] opacity-30 pointer-events-none" />
            <div className="relative z-10">
              <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-none bg-primary/10 border border-primary glow-primary">
                <Trophy className="h-10 w-10 text-primary" />
              </div>
              <h1 className="mb-3 text-2xl font-black font-display uppercase tracking-tighter text-shadow-neon-cyan">
                Daily contest
              </h1>
              <p className="mb-8 text-muted-foreground">
                Create a free account to play the daily contest, keep your streak, and appear on the leaderboard.
              </p>
              <div className="flex flex-col gap-3">
                <Link href="/sign-up">
                  <Button className="w-full glow-primary font-bold tracking-widest uppercase text-xs bg-primary text-primary-foreground hover:bg-primary/90 rounded-none" data-testid="button-daily-sign-up">
                    Create account
                  </Button>
                </Link>
                <Link href="/sign-in">
                  <Button variant="outline" className="w-full font-bold tracking-widest uppercase text-xs border-primary/40 text-primary hover:bg-primary/10 rounded-none" data-testid="button-daily-sign-in">
                    I already have an account
                  </Button>
                </Link>
                <Link href={`/play/${slug}/unlimited`}>
                  <Button variant="ghost" className="w-full font-mono uppercase tracking-widest text-xs text-muted-foreground hover:text-white" data-testid="button-daily-practice-instead">
                    Play without an account
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || (isDailyMode && !authLoaded)) {
    return (
      <div className="flex min-h-[100dvh] flex-col bg-background">
        <Navbar />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center animate-pulse">
            <div className="flex justify-center mb-6">
              <Zap className="h-12 w-12 text-primary" />
            </div>
            <p className="text-xl font-mono text-primary uppercase tracking-widest">LOADING...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="flex min-h-[100dvh] flex-col bg-background">
        <Navbar />
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="text-center border-2 border-primary/30 bg-primary/5 p-16 animate-pulse">
            <p className="text-xl font-mono text-primary mb-6 uppercase tracking-widest">PUZZLE NOT AVAILABLE</p>
            <Link href={`/games/${slug}`}>
              <Button variant="outline" className="font-mono uppercase tracking-widest text-primary border-primary hover:bg-primary/10 rounded-none">ABORT</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (puzzle.alreadyPlayed && isDailyMode && !completed) {
    return (
      <div className="flex min-h-[100dvh] flex-col bg-background">
        <Navbar />
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="w-full max-w-md text-center border border-primary/30 p-8 relative overflow-hidden bg-background">
            <div className="absolute inset-0 bg-[linear-gradient(hsl(189_100%_50%_/_0.1)_1px,_transparent_1px),_linear-gradient(90deg,_hsl(189_100%_50%_/_0.1)_1px,_transparent_1px)] bg-[size:20px_20px] opacity-30 pointer-events-none" />
            
            <div className="relative z-10">
              <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-none bg-primary/10 border border-primary glow-primary">
                <CheckCircle2 className="h-10 w-10 text-primary" />
              </div>
              <h1 className="mb-2 text-3xl font-black font-display uppercase tracking-widest text-shadow-neon-cyan">
                CHALLENGE BEATEN
              </h1>
              <p className="mb-8 text-muted-foreground font-mono">
                YOU ALREADY PLAYED TODAY. COME BACK TOMORROW.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href={`/games/${slug}`} className="flex-1">
                  <Button variant="outline" className="w-full h-12 rounded-none font-bold uppercase tracking-widest border-primary/50 text-primary hover:bg-primary/10">BACK</Button>
                </Link>
                <Link href={`/play/${slug}/unlimited`} className="flex-1">
                  <Button className="w-full h-12 rounded-none font-bold uppercase tracking-widest bg-secondary text-secondary-foreground hover:bg-secondary/90 glow-secondary">PLAY AGAIN</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentRound = puzzle.rounds[currentRoundIndex];
  const roundTime = Math.floor((Date.now() - roundStartTime) / 1000);
  const isLastRound = currentRoundIndex === puzzle.rounds.length - 1;

  const handleChoiceClick = (choice: string) => {
    // Lock immediately on first tap — selectedChoice doubles as the
    // "answer in flight" flag so the UI responds instantly while the
    // server round-trip completes.
    if (feedback || selectedChoice) return;
    setSelectedChoice(choice);
    const roundTimeMs = Date.now() - roundStartTime;

    submitGuess.mutate(
      {
        puzzleId: puzzle.id,
        data: {
          roundIndex: currentRoundIndex,
          choice,
          timeMs: roundTimeMs,
        },
      },
      {
        onSuccess: (result) => {
          setFeedback(result);
          setScore((prev) => prev + result.pointsEarned);
          if (result.correct) {
            setCorrectCount((prev) => prev + 1);
          }
          setRoundResults((prev) => [
            ...prev,
            { ...result, choice, prompt: puzzle.rounds[currentRoundIndex]?.prompt ?? '' },
          ]);

          // Tally exact per-round time — the ticking timeMs state is only a
          // display refresher and would drift by up to a second per round.
          elapsedRef.current += roundTimeMs;
          setTimeout(() => {
            if (isLastRound) {
              const totalTimeMs = elapsedRef.current;
              completePuzzle.mutate(
                {
                  puzzleId: puzzle.id,
                  data: {
                    score: score + result.pointsEarned,
                    correctCount: correctCount + (result.correct ? 1 : 0),
                    totalRounds: puzzle.rounds.length,
                    timeMs: totalTimeMs,
                  },
                },
                {
                  onSuccess: (playRes) => {
                    setPlayResult(playRes);
                    setCompleted(true);
                    // Streak badges live on /api/me — refetch so the navbar and
                    // profile pick up any badge unlocked by this play, and
                    // celebrate newly crossed milestones in the moment.
                    queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
                    if (playRes?.newBadges && playRes.newBadges.length > 0) {
                      setUnlockedBadges(playRes.newBadges);
                    }
                  },
                }
              );
            } else {
              setCurrentRoundIndex((prev) => prev + 1);
              setFeedback(null);
              setSelectedChoice(null);
            }
          }, 2000);
        },
        onError: (error: any) => {
          toast({
            title: 'Error',
            description: error.message || 'Failed to submit guess',
            variant: 'destructive',
          });
          setFeedback(null);
          setSelectedChoice(null);
        },
      }
    );
  };

  const handleShare = () => {
    if (playResult?.shareText) {
      navigator.clipboard.writeText(playResult.shareText);
      toast({ title: 'COPIED TO CLIPBOARD', className: 'border-primary bg-background text-primary rounded-none font-mono uppercase tracking-widest glow-primary' });
    }
  };

  const handlePlayAgain = () => {
    if (isDailyMode) {
      setLocation(`/games/${slug}`);
    } else {
      refetchUnlimited();
      setCurrentRoundIndex(0);
      setTimeMs(0);
      setScore(0);
      setCorrectCount(0);
      setRoundStartTime(Date.now());
      elapsedRef.current = 0;
      setRoundResults([]);
      setShowRecap(false);
      setCompleted(false);
      setPlayResult(null);
      setUnlockedBadges([]);
      setSelectedChoice(null);
      setFeedback(null);
      setRevealProgress(0);
    }
  };

  if (completed && playResult) {
    return (
      <div className="flex min-h-[100dvh] flex-col bg-background relative overflow-hidden">
        {unlockedBadges.length > 0 && (
          <BadgeUnlockCelebration milestones={unlockedBadges} onDismiss={() => setUnlockedBadges([])} />
        )}
        <div className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[100vw] h-[100vw] bg-primary/10 blur-[150px] rounded-full pointer-events-none" />
        <Navbar />
        <div className="flex flex-1 items-center justify-center p-4 relative z-10">
          <div className="w-full max-w-lg animate-slide-up">
            <div className="mb-10 text-center">
              <div className="mb-8 inline-flex h-24 w-24 items-center justify-center rounded-none bg-gradient-to-br from-primary to-secondary glow-primary border-2 border-primary/50 rotate-45">
                <Trophy className="h-10 w-10 text-background fill-background -rotate-45" />
              </div>
              <h1 className="mb-2 text-5xl font-black tracking-tighter uppercase font-display text-shadow-neon-cyan">
                {correctCount === puzzle.rounds.length ? 'Flawless Victory' : 'Nice Work'}
              </h1>
              <p className="text-xl font-mono text-muted-foreground uppercase tracking-widest mt-4">
                {correctCount} / {puzzle.rounds.length} CORRECT
              </p>
            </div>

            <div className="mb-8 space-y-1 bg-background/80 backdrop-blur-md border border-primary/30 p-2 relative">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent" />
              
              <div className="flex items-center justify-between bg-primary/10 p-4">
                <span className="font-mono text-muted-foreground uppercase tracking-widest">
                  FINAL SCORE
                  {isDailyMode && (
                    <span className="ml-2 bg-secondary/20 border border-secondary/50 px-1.5 py-0.5 text-[10px] font-bold text-secondary" data-testid="badge-daily-2x">
                      2X DAILY
                    </span>
                  )}
                </span>
                <span className="text-4xl font-black font-display text-primary tracking-tighter glow-primary text-shadow-neon-cyan">{playResult.score}</span>
              </div>
              
              <Show when="signed-in">
                <div className="space-y-1">
                  <div className="flex items-center justify-between bg-primary/5 p-4">
                    <span className="font-mono text-muted-foreground uppercase tracking-widest">XP EARNED</span>
                    <span className="font-bold text-secondary font-mono text-xl">+{playResult.xpEarned}</span>
                  </div>
                  <div className="bg-primary/5 p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-muted-foreground uppercase tracking-widest">CURRENT STREAK</span>
                      <span className="flex items-center gap-2 font-bold text-primary font-mono text-xl">
                        <Flame className="h-5 w-5" />
                        {playResult.currentStreak}
                      </span>
                    </div>
                    {isDailyMode && playResult.newBadges && playResult.newBadges.length > 0 && (
                      <div className="mt-3 border border-secondary/60 bg-secondary/10 p-3 text-center glow-secondary" data-testid="banner-new-badge">
                        <p className="mb-2 text-xs font-mono uppercase tracking-widest text-secondary animate-pulse">
                          NEW BADGE{playResult.newBadges.length > 1 ? 'S' : ''} UNLOCKED
                        </p>
                        <StreakBadges milestones={playResult.newBadges} size="md" className="justify-center" />
                      </div>
                    )}
                    {isDailyMode && playResult.nextBadgeMilestone != null && playResult.nextBadgeDaysLeft != null && (
                      <p className="mt-2 text-xs font-mono uppercase tracking-widest text-primary" data-testid="text-next-badge-hint">
                        {playResult.nextBadgeDaysLeft} more day{playResult.nextBadgeDaysLeft === 1 ? '' : 's'} to your {playResult.nextBadgeMilestone}-day badge
                      </p>
                    )}
                    {isDailyMode && playResult.currentStreak > 0 && (
                      <p className="mt-2 text-xs font-mono uppercase tracking-widest text-secondary" data-testid="text-streak-nudge">
                        Come back tomorrow to keep it alive — streak days earn bonus XP
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-between bg-primary/5 p-4">
                    <span className="font-mono text-muted-foreground uppercase tracking-widest">LEVEL</span>
                    <span className="font-bold font-mono text-xl">{playResult.level}</span>
                  </div>
                  {standing?.game && (
                    <Link href={`/games/${slug}`}>
                      <div className="flex items-center justify-between bg-primary/5 p-4 cursor-pointer transition-colors hover:bg-primary/15 group" data-testid="row-game-rank">
                        <span className="font-mono text-muted-foreground uppercase tracking-widest group-hover:text-primary transition-colors">THIS GAME</span>
                        <span className="flex items-center gap-2 font-bold font-mono text-xl text-primary">#{standing.game.rank} <span className="text-sm text-muted-foreground">of {standing.game.players}</span><ArrowRight className="h-4 w-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" /></span>
                      </div>
                    </Link>
                  )}
                  {standing?.alltime && (
                    <Link href="/leaderboards">
                      <div className="flex items-center justify-between bg-primary/5 p-4 cursor-pointer transition-colors hover:bg-secondary/15 group" data-testid="row-alltime-rank">
                        <span className="font-mono text-muted-foreground uppercase tracking-widest group-hover:text-secondary transition-colors">ALL-TIME RANK</span>
                        <span className="flex items-center gap-2 font-bold font-mono text-xl text-secondary">#{standing.alltime.rank} <span className="text-sm text-muted-foreground">of {standing.alltime.players}</span><ArrowRight className="h-4 w-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" /></span>
                      </div>
                    </Link>
                  )}
                  {playResult.leveledUp && (
                    <div className="bg-secondary/20 border border-secondary p-4 text-center glow-secondary mt-2">
                      <p className="font-bold font-display text-secondary text-2xl uppercase tracking-widest animate-pulse">LEVEL UP!</p>
                    </div>
                  )}
                </div>
              </Show>
              <Show when="signed-out">
                <div className="border border-dashed border-primary/50 bg-primary/5 p-6 text-center mt-2">
                  <p className="mb-4 text-sm font-mono uppercase tracking-widest text-primary">SIGN UP TO SAVE YOUR PROGRESS</p>
                  <Link href="/sign-up">
                    <Button className="w-full h-12 rounded-none font-bold uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 glow-primary">CREATE ACCOUNT</Button>
                  </Link>
                </div>
              </Show>
            </div>

            {roundResults.length > 0 && (
              <div className="mb-8 border border-primary/30 bg-background/80 backdrop-blur-md">
                <button
                  onClick={() => setShowRecap((v) => !v)}
                  className="flex w-full items-center justify-between p-4 font-mono text-sm uppercase tracking-widest text-primary hover:bg-primary/10 transition-colors"
                  data-testid="button-toggle-recap"
                >
                  <span>Review your answers</span>
                  {showRecap ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {showRecap && (
                  <div className="space-y-1 border-t border-primary/20 p-2">
                    {roundResults.map((r, i) => (
                      <div key={i} className="bg-primary/5 p-3" data-testid={`recap-round-${i}`}>
                        <div className="flex items-start gap-3">
                          {r.correct ? (
                            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-400" />
                          ) : (
                            <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-bold leading-snug">{r.prompt}</p>
                            <p className="mt-1 text-sm text-muted-foreground">
                              Your pick: <span className={r.correct ? 'text-green-400' : 'text-red-400'}>{r.choice}</span>
                              {!r.correct && (
                                <>
                                  {' '}· Answer: <span className="text-foreground">{r.correctAnswer}</span>
                                </>
                              )}
                            </p>
                            {r.fact && (
                              <p className="mt-1 text-sm text-primary/90">{r.fact}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              <Button
                size="lg"
                className="w-full h-14 rounded-none font-display text-lg uppercase tracking-widest font-bold bg-primary text-primary-foreground hover:bg-primary/90 glow-primary shadow-[0_0_30px_rgba(0,217,255,0.3)] transition-transform hover:-translate-y-1"
                onClick={handleShare}
                data-testid="button-share"
              >
                <Share2 className="mr-3 h-5 w-5" />
                SHARE RESULT
              </Button>
              <div className="grid grid-cols-2 gap-4">
                <Link href="/">
                  <Button variant="outline" className="w-full h-14 rounded-none font-bold uppercase tracking-widest border-primary/30 text-primary hover:bg-primary/10 transition-colors" data-testid="button-home">
                    <Home className="mr-2 h-5 w-5" />
                    BASE
                  </Button>
                </Link>
                <Button onClick={handlePlayAgain} className="w-full h-14 rounded-none font-bold uppercase tracking-widest bg-secondary text-secondary-foreground hover:bg-secondary/90 glow-secondary" data-testid="button-play-again">
                  <RotateCcw className="mr-2 h-5 w-5" />
                  RETRY
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background">
      <Navbar />
      
      {/* HUD Bar */}
      <div className="border-b-2 border-primary/30 bg-background/90 backdrop-blur-md sticky top-[80px] z-40 shadow-[0_4px_30px_-10px_rgba(0,217,255,0.2)]">
        <div className="mx-auto max-w-4xl px-4 py-3">
          <div className="mb-3 flex items-center justify-between">
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary font-mono text-xs uppercase tracking-widest rounded-none px-3 py-1">
              TARGET {currentRoundIndex + 1} / {puzzle.rounds.length}
            </Badge>
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-2 font-mono text-lg font-bold text-secondary">
                <Timer className="h-5 w-5" />
                {roundTime}s
              </span>
              <span className="font-bold text-primary font-display text-2xl tracking-tighter text-shadow-neon-cyan">{score} PTS</span>
            </div>
          </div>
          <div className="h-2 w-full bg-primary/10 overflow-hidden relative">
            <div 
              className="absolute top-0 left-0 h-full bg-primary shadow-[0_0_10px_rgba(0,217,255,0.8)] transition-all duration-300"
              style={{ width: `${((currentRoundIndex + 1) / puzzle.rounds.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-4xl">
          <RoundContent
            round={currentRound}
            gameType={puzzle.gameType}
            revealProgress={revealProgress}
            feedback={feedback}
            selectedChoice={selectedChoice}
            onChoiceClick={handleChoiceClick}
          />
        </div>
      </div>
    </div>
  );
}

interface RoundContentProps {
  round: PuzzleRound;
  gameType: string;
  revealProgress: number;
  feedback: GuessResult | null;
  selectedChoice: string | null;
  onChoiceClick: (choice: string) => void;
}

/**
 * Zooming logo that also starts heavily pixelated and sharpens as the
 * reveal progresses. Rendered on a canvas: the image is drawn at a low
 * resolution and upscaled with smoothing off, so early frames are chunky
 * mosaic blocks that resolve into the crisp logo.
 */
/**
 * Pick a random zoom origin anchored on ACTUAL logo pixels, so the reveal can
 * start from a corner/edge of the artwork instead of always dead center —
 * and never from empty transparent space (e.g. the hollow middle of a
 * horseshoe logo). Requires CORS-readable pixels; returns null otherwise.
 */
function pickLogoOrigin(img: HTMLImageElement): string | null {
  try {
    const S = 64;
    const c = document.createElement('canvas');
    c.width = S;
    c.height = S;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    // Same contain-fit as the main canvas so percentages line up.
    const ratio = Math.min(S / img.width, S / img.height);
    const dw = img.width * ratio;
    const dh = img.height * ratio;
    ctx.drawImage(img, (S - dw) / 2, (S - dh) / 2, dw, dh);
    const data = ctx.getImageData(0, 0, S, S).data; // throws if tainted
    const candidates: number[] = [];
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const i = (y * S + x) * 4;
        // Solid, visibly bright pixel (skip transparency and near-black,
        // which vanish against the black stage).
        if (data[i + 3] > 60 && data[i] + data[i + 1] + data[i + 2] > 90) {
          candidates.push(y * S + x);
        }
      }
    }
    if (candidates.length < 20) return null;
    const pick = candidates[Math.floor(Math.random() * candidates.length)];
    const px = ((pick % S) + 0.5) / S;
    const py = (Math.floor(pick / S) + 0.5) / S;
    // Keep the origin off the extreme edges so the zoom stays in frame.
    const clamp = (v: number) => Math.min(88, Math.max(12, v * 100));
    return `${clamp(px).toFixed(1)}% ${clamp(py).toFixed(1)}%`;
  } catch {
    return null; // tainted canvas (no CORS) — caller falls back to center
  }
}

function PixelatedZoomImage({ src, revealProgress, scale }: { src: string; revealProgress: number; scale: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [origin, setOrigin] = useState('center center');

  useEffect(() => {
    setLoaded(false);
    setOrigin('center center');
    // Wipe the previous round's image immediately — leaving it on screen
    // while the next one loads reads as a stuck/stale question.
    const stale = canvasRef.current;
    if (stale) stale.getContext('2d')?.clearRect(0, 0, stale.width, stale.height);
    let cancelled = false;
    // Load in a pixel-readable way so we can pick a random start point on the
    // actual logo: try direct CORS first, then our same-origin image
    // pass-through, and finally a plain draw-only load (center origin).
    const proxied = `${import.meta.env.BASE_URL}api/imgproxy?src=${encodeURIComponent(src)}`;
    const load = (attempt: number) => {
      const img = new Image();
      const readable = attempt < 2;
      if (attempt === 0) img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (cancelled) return;
        imgRef.current = img;
        if (readable) {
          const o = pickLogoOrigin(img);
          if (o) setOrigin(o);
        }
        setLoaded(true);
      };
      img.onerror = () => {
        if (!cancelled && attempt < 2) load(attempt + 1);
      };
      img.src = attempt === 1 ? proxied : src;
    };
    load(0);
    return () => {
      cancelled = true;
      imgRef.current = null;
    };
  }, [src]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !loaded) return;
    const SIZE = 512;
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // From ~28px blocks at 0% down to full resolution at 100%.
    const t = Math.min(revealProgress, 100) / 100;
    const blockSize = Math.max(1, Math.round(28 * Math.pow(1 - t, 1.7)));
    const lowW = Math.max(1, Math.floor(SIZE / blockSize));

    // Fit the image inside a square (like bg-contain).
    const ratio = Math.min(SIZE / img.width, SIZE / img.height);
    const dw = img.width * ratio;
    const dh = img.height * ratio;
    const dx = (SIZE - dw) / 2;
    const dy = (SIZE - dh) / 2;

    const off = document.createElement('canvas');
    off.width = lowW;
    off.height = lowW;
    const offCtx = off.getContext('2d');
    if (!offCtx) return;
    offCtx.imageSmoothingEnabled = true;
    offCtx.drawImage(img, (dx / SIZE) * lowW, (dy / SIZE) * lowW, (dw / SIZE) * lowW, (dh / SIZE) * lowW);

    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.imageSmoothingEnabled = blockSize <= 1;
    ctx.drawImage(off, 0, 0, lowW, lowW, 0, 0, SIZE, SIZE);
  }, [revealProgress, loaded]);

  // Roughly half the rounds hold color back (grayscale early, saturation
  // ramps in, full color by ~85%) — team colors are a huge tell. The other
  // half start in full color so the reveal doesn't ALWAYS open gray.
  const holdColor = useMemo(() => Math.random() < 0.5, [src]);
  const t = Math.min(revealProgress, 100) / 100;
  const grayscale = holdColor ? (t < 0.35 ? 1 : Math.max(0, 1 - (t - 0.35) / 0.5)) : 0;

  return (
    <canvas
      ref={canvasRef}
      // No drop-shadow here: stacking filters on an animating canvas is
      // expensive and causes jank on lower-end devices.
      className="absolute inset-0 h-full w-full"
      style={{
        transform: `scale(${scale})`,
        transformOrigin: origin,
        filter: `grayscale(${Math.round(grayscale * 100)}%)`,
        transition: 'transform 300ms',
        willChange: 'transform, filter',
        opacity: loaded ? 1 : 0,
      }}
      data-testid="pixelated-logo"
    />
  );
}

function RoundContent({ round, gameType, revealProgress, feedback, selectedChoice, onChoiceClick }: RoundContentProps) {
  const renderImage = () => {
    if (!round.imageUrl) return null;

    if (gameType === 'logo-reveal' || gameType === 'face-zoom') {
      // Face zoom starts MUCH tighter (a single facial feature) so the early
      // guess is genuinely hard; logos keep the gentler 4x start.
      const startScale = gameType === 'face-zoom' ? 9 : 4;
      const scale = startScale - (revealProgress / 100) * (startScale - 1);
      // Player cutouts are full-body; anchor the zoom near the head.
      const origin = gameType === 'face-zoom' ? 'center 12%' : 'center center';
      return (
        <div className="relative mb-4 sm:mb-6 overflow-hidden border-2 border-primary bg-black aspect-square max-h-44 sm:max-h-64 mx-auto shadow-[0_0_40px_rgba(0,217,255,0.2)]">
          <div className="absolute inset-0 z-10 bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.5)_50%)] bg-[length:100%_4px] pointer-events-none" />
          {gameType === 'logo-reveal' ? (
            <PixelatedZoomImage
              src={round.imageUrl}
              revealProgress={feedback ? 100 : revealProgress}
              scale={feedback ? 1 : scale}
            />
          ) : (
          <div
            // Remount per image: a background-image swap keeps painting the
            // old picture until the new one decodes — stale-round flash.
            key={round.imageUrl}
            className="absolute inset-0 bg-contain bg-center bg-no-repeat transition-transform duration-300 filter drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]"
            style={{
              backgroundImage: `url(${round.imageUrl})`,
              transform: `scale(${scale})`,
              transformOrigin: origin,
            }}
          />
          )}
          {/* Target reticle overlay */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-20 opacity-30">
            <Crosshair className="w-32 h-32 text-primary animate-spin-slow" style={{ animationDuration: '10s' }} />
          </div>
        </div>
      );
    }

    return (
      <div className="mb-4 sm:mb-6 relative group">
        <div className="absolute inset-0 bg-primary/20 blur-xl pointer-events-none opacity-50" />
        <img
          src={round.imageUrl}
          alt={round.imageHint || 'Puzzle image'}
          className="mx-auto max-h-44 sm:max-h-64 border-2 border-primary/50 object-contain relative z-10 bg-black/50 p-2 shadow-[0_0_30px_rgba(0,0,0,0.5)]"
        />
      </div>
    );
  };

  return (
    <div className={feedback ? (feedback.correct ? 'animate-flash-correct' : 'animate-flash-incorrect') : 'animate-slide-up'}>
      <div className="mb-3 sm:mb-5 text-center relative">
        <h2 className="mb-1 sm:mb-2 text-xl font-black sm:text-3xl font-display uppercase tracking-tighter text-shadow-neon-cyan leading-[1.1]">
          {round.prompt}
        </h2>
        {round.imageHint && (
          <p className="text-sm font-mono text-secondary uppercase tracking-wider mb-2">
            {round.imageHint}
          </p>
        )}
        <div className="inline-block border border-primary/30 bg-primary/10 px-3 py-0.5 text-xs font-mono uppercase tracking-wider text-primary">
          Worth up to {round.maxPoints} pts
        </div>
      </div>

      {renderImage()}

      {(gameType === 'higher-lower' || gameType === 'stat-duel' || gameType === 'player-older') &&
      round.choices.length === 2 ? (
        <div className="grid grid-cols-2 gap-2 sm:gap-6">
          {round.choices.map((choice) => {
            const isSelected = selectedChoice === choice;
            const showResult = feedback && isSelected;
            return (
              <button
                key={choice}
                onClick={() => onChoiceClick(choice)}
                disabled={!!feedback || !!selectedChoice}
                className={`group relative rounded-none border-2 p-4 sm:p-6 text-center text-lg sm:text-2xl font-black uppercase font-display tracking-tighter transition-all duration-150 ${
                  showResult
                    ? feedback.correct
                      ? 'border-green-500 bg-green-500/20 text-green-400 glow-correct'
                      : 'border-red-500 bg-red-500/20 text-red-400 glow-incorrect'
                    : isSelected
                      ? 'border-primary bg-primary/20 text-primary glow-primary scale-[0.98]'
                      : 'border-primary/50 bg-background text-foreground hover:border-primary hover:bg-primary/10 hover:-translate-y-2 hover:shadow-[0_10px_30px_-10px_rgba(0,217,255,0.4)] active:scale-95'
                }`}
                data-testid={`choice-${choice}`}
              >
                {choice}
                {showResult && (
                  <div className="absolute top-4 right-4 animate-pop-score">
                    {feedback.correct ? (
                      <CheckCircle2 className="h-10 w-10 text-green-400 drop-shadow-[0_0_10px_rgba(74,222,128,0.8)]" />
                    ) : (
                      <XCircle className="h-10 w-10 text-red-400 drop-shadow-[0_0_10px_rgba(248,113,113,0.8)]" />
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:gap-4">
          {round.choices.map((choice) => {
            const isSelected = selectedChoice === choice;
            const showResult = feedback && isSelected;
            return (
              <button
                key={choice}
                onClick={() => onChoiceClick(choice)}
                disabled={!!feedback || !!selectedChoice}
                className={`group relative rounded-none border-2 p-3 sm:p-4 text-left font-bold text-sm sm:text-lg leading-tight transition-all duration-150 ${
                  showResult
                    ? feedback.correct
                      ? 'border-green-500 bg-green-500/20 text-green-400 glow-correct'
                      : 'border-red-500 bg-red-500/20 text-red-400 glow-incorrect'
                    : isSelected
                      ? 'border-primary bg-primary/20 text-primary glow-primary scale-[0.98]'
                      : 'border-primary/30 bg-background hover:border-primary hover:bg-primary/10 hover:shadow-[0_0_20px_rgba(0,217,255,0.2)] active:scale-95'
                }`}
                data-testid={`choice-${choice}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display tracking-tight uppercase">{choice}</span>
                  {showResult && (
                    <div className="animate-pop-score">
                      {feedback.correct ? (
                        <CheckCircle2 className="h-8 w-8 text-green-400 drop-shadow-[0_0_10px_rgba(74,222,128,0.8)]" />
                      ) : (
                        <XCircle className="h-8 w-8 text-red-400 drop-shadow-[0_0_10px_rgba(248,113,113,0.8)]" />
                      )}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="min-h-24 mt-8 flex flex-col items-center justify-center gap-3">
        {feedback && !feedback.correct && (
          <div className="rounded-none border-l-4 border-red-500 bg-red-500/10 px-8 py-4 text-center animate-slide-up">
            <p className="text-sm font-mono text-muted-foreground uppercase tracking-widest">
              CORRECT ANSWER: <span className="font-bold text-foreground text-lg ml-2 text-shadow-neon-cyan">{feedback.correctAnswer}</span>
            </p>
          </div>
        )}

        {feedback && feedback.correct && (
          <div className="text-center animate-pop-score">
            <div className="inline-flex items-center gap-3 rounded-none bg-green-500/20 border border-green-500 px-8 py-3 text-green-400 font-black font-display text-3xl uppercase tracking-tighter drop-shadow-[0_0_10px_rgba(74,222,128,0.5)]">
              +{feedback.pointsEarned} PTS
            </div>
          </div>
        )}

        {feedback?.fact && (
          <p
            className="max-w-xl text-center text-sm text-primary/90 animate-slide-up border border-primary/30 bg-primary/5 px-4 py-2"
            data-testid="text-round-fact"
          >
            {feedback.fact}
          </p>
        )}
      </div>
    </div>
  );
}
